import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callerProfiles } from '@/api/callerProfiles';
import { points } from '@/api/points';
import type { PointsLedgerRow, AdjustPointsBody } from '@/api/points';
import type { CallerProfileRow } from '@/api/callerProfiles';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Coins, TrendingUp, TrendingDown, Plus, Minus, Search,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';

const ADJUST_ROLES = ['super_admin', 'reseller_owner', 'office_manager'];

export function PointsPage() {
  const user = useCurrentUser();
  const canAdjust = ADJUST_ROLES.includes(user.role);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: callersData, isLoading } = useQuery({
    queryKey: ['caller-profiles', search],
    queryFn: () => callerProfiles.list({ search: search.trim() || undefined, limit: 200 }),
  });
  const callers = callersData?.rows ?? [];

  const effectiveId = selectedId ?? callers[0]?.id ?? null;
  const selected = callers.find((c) => c.id === effectiveId) ?? null;

  // Rank by balance desc for the "big spenders/earners" view
  const ranked = useMemo(
    () => [...callers].sort((a, b) => b.points_balance - a.points_balance),
    [callers],
  );

  return (
    <>
      <PageHeader
        eyebrow="Engagement"
        title="Points &amp; credits"
        description="Caller rewards ledger. Every change writes an audit row — balances can't drift silently."
      />

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading callers…</div>
      ) : callers.length === 0 ? (
        <EmptyCallersCard />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
          <CallerRail
            callers={ranked}
            activeId={effectiveId}
            onSelect={setSelectedId}
            search={search}
            onSearch={setSearch}
          />
          {selected && (
            <CallerLedgerPanel caller={selected} canAdjust={canAdjust} />
          )}
        </div>
      )}
    </>
  );
}

function EmptyCallersCard() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Coins className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No callers on file yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          Points attach to caller profiles. Create one from{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">Accounts → Callers</code>{' '}
          to start awarding.
        </p>
      </CardContent>
    </Card>
  );
}

function CallerRail({
  callers, activeId, onSelect, search, onSearch,
}: {
  callers: CallerProfileRow[];
  activeId: string | null;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (s: string) => void;
}) {
  return (
    <aside>
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Callers by balance</CardTitle>
          <CardDescription>{callers.length} on file</CardDescription>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Name, email, phone…"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 max-h-[calc(100vh-16rem)] overflow-y-auto">
          <ul className="divide-y divide-border">
            {callers.map((c) => {
              const active = c.id === activeId;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(c.id)}
                    className={cn(
                      'w-full text-left px-4 py-3 transition-colors',
                      'flex items-center justify-between gap-3',
                      active
                        ? 'bg-primary/5 border-l-2 border-primary'
                        : 'border-l-2 border-transparent hover:bg-secondary/40',
                    )}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">
                        {c.display_name || formatRef(c.ref_number, 'CAL')}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {c.email || `Ref ${formatRef(c.ref_number, 'CAL')}`}
                      </div>
                    </div>
                    <div className={cn(
                      'tabular-nums font-mono text-sm shrink-0',
                      c.points_balance > 0 ? 'text-emerald-700'
                        : c.points_balance < 0 ? 'text-rose-700'
                          : 'text-muted-foreground',
                    )}>
                      {c.points_balance.toLocaleString()}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </aside>
  );
}

function CallerLedgerPanel({
  caller, canAdjust,
}: {
  caller: CallerProfileRow; canAdjust: boolean;
}) {
  const [adjustOpen, setAdjustOpen] = useState<'credit' | 'debit' | null>(null);

  const { data: summary } = useQuery({
    queryKey: ['points-summary', caller.id],
    queryFn: () => points.getSummary(caller.id),
  });
  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['points-ledger', caller.id],
    queryFn: () => points.listLedger(caller.id, { limit: 100 }),
  });
  const rows = ledgerData?.rows ?? [];

  return (
    <section className="min-w-0 space-y-4">
      <Card className="overflow-hidden">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">
                {caller.display_name || `Caller ${formatRef(caller.ref_number, 'CAL')}`}
              </CardTitle>
              <CardDescription>
                {caller.email ? `${caller.email} · ` : ''}Ref {formatRef(caller.ref_number, 'CAL')}
              </CardDescription>
            </div>
            {canAdjust && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setAdjustOpen('credit')}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Credit
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAdjustOpen('debit')}>
                  <Minus className="h-3.5 w-3.5 mr-1" /> Debit
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="grid grid-cols-3 gap-6">
            <Stat
              label="Balance"
              value={(summary?.balance ?? caller.points_balance).toLocaleString()}
              valueClassName={cn(
                'text-3xl font-bold tabular-nums',
                (summary?.balance ?? caller.points_balance) >= 0 ? 'text-emerald-700' : 'text-rose-700',
              )}
            />
            <Stat
              label="Lifetime earned"
              value={(summary?.lifetime_earned ?? 0).toLocaleString()}
              icon={TrendingUp}
              iconClassName="text-emerald-500"
              valueClassName="text-xl font-semibold tabular-nums"
            />
            <Stat
              label="Lifetime spent"
              value={(summary?.lifetime_spent ?? 0).toLocaleString()}
              icon={TrendingDown}
              iconClassName="text-rose-500"
              valueClassName="text-xl font-semibold tabular-nums"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Ledger</CardTitle>
          <CardDescription>
            Append-only audit trail. {summary?.event_count ?? 0} event{summary?.event_count === 1 ? '' : 's'}.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {ledgerLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No events yet. Awards from IVR nodes and manual adjustments will appear here.
            </div>
          ) : (
            <LedgerTable rows={rows} />
          )}
        </CardContent>
      </Card>

      {canAdjust && adjustOpen && (
        <AdjustDialog
          caller={caller}
          mode={adjustOpen}
          onClose={() => setAdjustOpen(null)}
        />
      )}
    </section>
  );
}

function Stat({
  label, value, icon: Icon, valueClassName, iconClassName,
}: {
  label: string; value: string;
  icon?: typeof TrendingUp;
  valueClassName?: string;
  iconClassName?: string;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
        {Icon && <Icon className={cn('h-3 w-3', iconClassName)} />}
        {label}
      </div>
      <div className={valueClassName}>{value}</div>
    </div>
  );
}

function LedgerTable({ rows }: { rows: PointsLedgerRow[] }) {
  return (
    <div className="divide-y divide-border">
      <div className="grid grid-cols-[5rem_1fr_9rem_6rem] items-center px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-slate-50">
        <span className="text-right">Delta</span>
        <span className="pl-4">Reason</span>
        <span>Source</span>
        <span className="text-right">When</span>
      </div>
      {rows.map((r) => (
        <LedgerRow key={r.id} row={r} />
      ))}
    </div>
  );
}

function LedgerRow({ row }: { row: PointsLedgerRow }) {
  const positive = row.delta > 0;
  return (
    <div className="grid grid-cols-[5rem_1fr_9rem_6rem] items-center px-4 py-2.5 text-sm hover:bg-slate-50/60">
      <span className={cn(
        'text-right font-mono font-semibold tabular-nums',
        positive ? 'text-emerald-700' : 'text-rose-700',
      )}>
        {positive ? '+' : ''}{row.delta.toLocaleString()}
      </span>
      <span className="pl-4 truncate text-foreground">{row.reason}</span>
      <span className="truncate">
        <SourceChip kind={row.source_kind} />
      </span>
      <time className="text-right text-[11px] text-muted-foreground tabular-nums" dateTime={row.created_at}>
        {new Date(row.created_at).toLocaleString(undefined, {
          month: 'short', day: 'numeric',
          hour: 'numeric', minute: '2-digit',
        })}
      </time>
    </div>
  );
}

function SourceChip({ kind }: { kind: string | null }) {
  if (!kind) return <span className="text-muted-foreground text-xs">—</span>;
  const styles: Record<string, string> = {
    ivr_node: 'bg-slate-100 text-slate-700',
    quiz: 'bg-indigo-100 text-indigo-700',
    enrollment: 'bg-amber-100 text-amber-800',
    admin: 'bg-primary/10 text-primary',
    redeem: 'bg-rose-100 text-rose-700',
    correction: 'bg-orange-100 text-orange-800',
  };
  const style = styles[kind] ?? 'bg-slate-100 text-slate-600';
  return (
    <span className={cn(
      'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
      style,
    )}>
      {kind.replace('_', ' ')}
    </span>
  );
}

function AdjustDialog({
  caller, mode, onClose,
}: {
  caller: CallerProfileRow;
  mode: 'credit' | 'debit';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const adjust = useMutation({
    mutationFn: (body: AdjustPointsBody) => points.adjust(caller.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['points-summary', caller.id] });
      qc.invalidateQueries({ queryKey: ['points-ledger', caller.id] });
      qc.invalidateQueries({ queryKey: ['caller-profiles'] });
      setAmount(''); setReason(''); setError(null);
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Adjustment failed.');
    },
  });

  const magnitude = Number(amount);
  const validAmount = Number.isFinite(magnitude) && Number.isInteger(magnitude) && magnitude > 0;
  const delta = mode === 'credit' ? magnitude : -magnitude;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'credit' ? 'Credit points' : 'Debit points'} —{' '}
            {caller.display_name || `Caller ${formatRef(caller.ref_number, 'CAL')}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-md border border-border bg-slate-50 px-3 py-2 text-xs flex items-center justify-between">
            <span className="text-muted-foreground">Current balance</span>
            <span className="font-mono tabular-nums font-semibold">
              {caller.points_balance.toLocaleString()}
            </span>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pts-amount">Amount (whole number)</Label>
            <Input
              id="pts-amount"
              type="number" min="1" step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 50"
              className="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pts-reason">Reason (required)</Label>
            <Input
              id="pts-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={mode === 'credit' ? 'e.g. promo bonus' : 'e.g. redemption for gift card'}
            />
            <p className="text-[11px] text-muted-foreground">
              Recorded in the audit ledger. Be specific — future-you will thank you.
            </p>
          </div>
          {validAmount && (
            <div className={cn(
              'rounded-md px-3 py-2 text-sm font-medium text-center',
              mode === 'credit' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800',
            )}>
              New balance: {(caller.points_balance + delta).toLocaleString()}
            </div>
          )}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={adjust.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={adjust.isPending || !validAmount || !reason.trim()}
            onClick={() => adjust.mutate({
              delta, reason: reason.trim(), source_kind: 'admin',
            })}
          >
            {adjust.isPending ? 'Saving…' : mode === 'credit' ? 'Credit' : 'Debit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
